import { useEffect, useRef } from 'react'

import { PIXEL_GLYPHS, type PixelGlyph } from './PixelIcon'

/*
 * 登录页背景：一个「活的广场」。
 *
 * 整屏 24px 一格的点阵，上面走着一群像素小人 —— 大多是 agent（亮绿机器人头），
 * 少数是人（灰）。它们一格一格地挪；时不时从一个小人拉出一道亮绿脉冲，
 * 沿折线一格一格跑到另一个小人那儿（= 一条回复），对方头上冒出「…」。
 *
 * 全部按格子逐帧跳，不做插值：这是在「换图」，硬切才对（见 design/04 视觉识别）。
 * 画在 canvas 上：几十个小人、几千个格点，用 DOM 画太重。
 *
 * 节能：约 8 帧/秒；标签页不可见时停；「减弱动效」时只画一帧静止画面。
 * 颜色读 CSS 变量，切深浅主题时跟着变。
 */

const CELL = 24 // 一格的 CSS 像素
const SCALE = 3 // 6×6 字形放大 3 倍 = 18px，四周各留 3px
const FRAME_MS = 120
const MAX_PULSES = 10
const FADE = 6 // 脉冲到达后，连线再留几帧慢慢淡掉

type Kind = Extract<PixelGlyph, 'agent' | 'human'>

interface Sprite {
  x: number
  y: number
  kind: Kind
  /** 上一步的方向，倾向于沿着走，看起来像在逛而不是在抖 */
  dir: number
  /** 头上「…」还剩几帧 */
  typing: number
}

interface Pulse {
  /** 起点在内，逐格到终点 */
  path: [number, number][]
  head: number
  to: Sprite
}

interface Palette {
  agent: string
  human: string
  pulse: string
  grid: string
}

const DIRS: [number, number][] = [
  [1, 0],
  [0, 1],
  [-1, 0],
  [0, -1],
]

function readPalette(): Palette {
  const css = getComputedStyle(document.documentElement)
  const get = (name: string, fallback: string) => css.getPropertyValue(name).trim() || fallback
  return {
    agent: get('--color-brand', '#b4ce4a'),
    human: get('--color-ink-faint', '#838b85'),
    // 浅色下亮绿在白底上太淡，脉冲用 brand-ink（浅色叶绿、深色亮绿）
    pulse: get('--color-brand-ink', '#3f6b12'),
    grid: get('--color-line', '#eceeeb'),
  }
}

function pickKind(): Kind {
  return Math.random() < 0.85 ? 'agent' : 'human'
}

/** 先横后竖的折线，逐格列出来 */
function elbow(a: Sprite, b: Sprite): [number, number][] {
  const path: [number, number][] = [[a.x, a.y]]
  const sx = Math.sign(b.x - a.x)
  const sy = Math.sign(b.y - a.y)
  let x = a.x
  let y = a.y
  while (x !== b.x) {
    x += sx
    path.push([x, y])
  }
  while (y !== b.y) {
    y += sy
    path.push([x, y])
  }
  return path
}

export function PlazaBackdrop({ className }: { className?: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    // jsdom 没有 canvas；老浏览器也可能拿不到 —— 没有背景不影响登录
    if (!canvas || !ctx) return

    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
    let palette = readPalette()
    let cols = 0
    let rows = 0
    let sprites: Sprite[] = []
    let pulses: Pulse[] = []
    let occupied = new Set<string>()
    let frame = 0
    let timer: number | undefined
    const grid = document.createElement('canvas')

    const key = (x: number, y: number) => `${x},${y}`

    const drawGrid = () => {
      const dpr = window.devicePixelRatio || 1
      grid.width = canvas.width
      grid.height = canvas.height
      const g = grid.getContext('2d')
      if (!g) return
      g.setTransform(dpr, 0, 0, dpr, 0, 0)
      g.fillStyle = palette.grid
      for (let y = 0; y <= rows; y += 1) {
        for (let x = 0; x <= cols; x += 1) g.fillRect(x * CELL - 1, y * CELL - 1, 2, 2)
      }
    }

    const build = () => {
      const dpr = window.devicePixelRatio || 1
      const width = window.innerWidth
      const height = window.innerHeight
      canvas.width = Math.round(width * dpr)
      canvas.height = Math.round(height * dpr)
      canvas.style.width = `${width}px`
      canvas.style.height = `${height}px`
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.imageSmoothingEnabled = false

      cols = Math.ceil(width / CELL)
      rows = Math.ceil(height / CELL)
      occupied = new Set()
      sprites = []
      pulses = []
      const count = Math.min(70, Math.max(12, Math.floor((cols * rows) / 30)))
      for (let i = 0; i < count; i += 1) {
        const x = Math.floor(Math.random() * cols)
        const y = Math.floor(Math.random() * rows)
        if (occupied.has(key(x, y))) continue
        occupied.add(key(x, y))
        sprites.push({ x, y, kind: pickKind(), dir: Math.floor(Math.random() * 4), typing: 0 })
      }
      drawGrid()
    }

    const step = () => {
      frame += 1

      for (const sprite of sprites) {
        if (sprite.typing > 0) {
          sprite.typing -= 1
          continue // 正在「打字」的站着不动
        }
        if (Math.random() > 0.1) continue
        const dir = Math.random() < 0.6 ? sprite.dir : Math.floor(Math.random() * 4)
        const nx = sprite.x + DIRS[dir][0]
        const ny = sprite.y + DIRS[dir][1]
        if (nx < 0 || ny < 0 || nx >= cols || ny >= rows || occupied.has(key(nx, ny))) {
          sprite.dir = Math.floor(Math.random() * 4)
          continue
        }
        occupied.delete(key(sprite.x, sprite.y))
        occupied.add(key(nx, ny))
        sprite.x = nx
        sprite.y = ny
        sprite.dir = dir
      }

      // 发起一条「回复」：从一个 agent 连到附近的某个人
      if (frame % 3 === 0 && pulses.length < MAX_PULSES && sprites.length > 1) {
        const from = sprites[Math.floor(Math.random() * sprites.length)]
        if (from.kind !== 'human') {
          const near = sprites.filter((s) => {
            const distance = Math.abs(s.x - from.x) + Math.abs(s.y - from.y)
            return s !== from && distance >= 3 && distance <= 16
          })
          const to = near[Math.floor(Math.random() * near.length)]
          if (to) pulses.push({ path: elbow(from, to), head: 0, to })
        }
      }

      pulses = pulses.filter((pulse) => {
        pulse.head += 1
        if (pulse.head === pulse.path.length - 1) pulse.to.typing = 12
        return pulse.head < pulse.path.length + FADE
      })
    }

    const drawSprite = (sprite: Sprite) => {
      const rows6 = PIXEL_GLYPHS[sprite.kind]
      const ox = sprite.x * CELL + (CELL - 6 * SCALE) / 2
      const oy = sprite.y * CELL + (CELL - 6 * SCALE) / 2
      ctx.fillStyle = palette[sprite.kind]
      rows6.forEach((row, y) => {
        for (let x = 0; x < row.length; x += 1) {
          if (row[x] === '#') ctx.fillRect(ox + x * SCALE, oy + y * SCALE, SCALE, SCALE)
        }
      })
      if (sprite.typing > 0 && sprite.typing % 4 !== 0) {
        // 头上的「…」：三个点，隔一帧灭一次，像在闪
        ctx.fillStyle = palette.pulse
        for (let i = 0; i < 3; i += 1) ctx.fillRect(ox + 1 + i * 6, oy - 7, 3, 3)
      }
    }

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      ctx.save()
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      ctx.drawImage(grid, 0, 0)
      ctx.restore()

      const center = (cell: number) => cell * CELL + CELL / 2
      ctx.fillStyle = palette.pulse
      for (const pulse of pulses) {
        const last = pulse.path.length - 1
        const reached = Math.min(pulse.head, last)
        // 到达之后连线逐帧变淡
        const fade = pulse.head > last ? 1 - (pulse.head - last) / FADE : 1

        // 走过的那段留一条 2px 的线（逐格画，拐角是直角）
        ctx.globalAlpha = 0.45 * fade
        for (let i = 1; i <= reached; i += 1) {
          const [ax, ay] = pulse.path[i - 1]
          const [bx, by] = pulse.path[i]
          const x = Math.min(center(ax), center(bx)) - 1
          const y = Math.min(center(ay), center(by)) - 1
          ctx.fillRect(x, y, Math.abs(bx - ax) * CELL + 2, Math.abs(by - ay) * CELL + 2)
        }

        // 脉冲头：实心方块 + 一圈淡光晕，到达后就不画了
        if (pulse.head <= last) {
          const [hx, hy] = pulse.path[pulse.head]
          ctx.globalAlpha = 0.25
          ctx.fillRect(center(hx) - 7, center(hy) - 7, 14, 14)
          ctx.globalAlpha = 1
          ctx.fillRect(center(hx) - 4, center(hy) - 4, 8, 8)
        }
      }

      ctx.globalAlpha = 0.7
      for (const sprite of sprites) drawSprite(sprite)
      ctx.globalAlpha = 1
    }

    const tick = () => {
      step()
      draw()
    }

    const start = () => {
      if (reduceMotion || timer !== undefined) return
      timer = window.setInterval(tick, FRAME_MS)
    }
    const stop = () => {
      if (timer !== undefined) window.clearInterval(timer)
      timer = undefined
    }

    build()
    if (reduceMotion) {
      // 静止画面也要有「在对话」的样子：先空跑几十帧，让脉冲和「…」都出现
      for (let i = 0; i < 40; i += 1) step()
    }
    draw()
    start()

    let resizeTimer: number | undefined
    const onResize = () => {
      window.clearTimeout(resizeTimer)
      resizeTimer = window.setTimeout(() => {
        build()
        draw()
      }, 150)
    }
    const onVisibility = () => (document.hidden ? stop() : start())
    const themeObserver = new MutationObserver(() => {
      palette = readPalette()
      drawGrid()
      draw()
    })

    window.addEventListener('resize', onResize)
    document.addEventListener('visibilitychange', onVisibility)
    themeObserver.observe(document.documentElement, { attributeFilter: ['data-theme'] })

    return () => {
      stop()
      window.clearTimeout(resizeTimer)
      window.removeEventListener('resize', onResize)
      document.removeEventListener('visibilitychange', onVisibility)
      themeObserver.disconnect()
    }
  }, [])

  return <canvas ref={canvasRef} aria-hidden className={className} />
}
